import React, {useCallback, useMemo, useRef} from 'react';

import {SectionFilter} from 'src/components/section-filter';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {NoResultPage} from 'src/modules/entitlements';

import {BoxLoading} from '@pb/kit-component-box-loading';
import Button, {FlatButton} from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {StickyElement} from '@pb/kit-component-sticky-element';
import {Pill, StripOfPills} from '@pb/kit-component-strip-of-pills';
import Typography from '@pb/kit-component-typography';

import {EmailFilter} from './email-filter';
import {FILTER_LABELS, ProvisioningFilters, getFilterCaption} from './provisioning-filters';
import {ProvisioningHistoryTable} from './provisioning-history-table';
import {ProvisioningPagination} from './provisioning-pagination';
import {useProvisioningHistory} from './use-provisioning-history';

import {
    provisioningContent, provisioningHeader, provisioningPills, provisioningSection, provisioningTitle,
} from './provisioning-history.module.styl';

import type {AdvancedFilterKey, AdvancedFilters} from './use-provisioning-history';

const HistorySection = ({accountId}: {accountId: number}) => {
    const history = useProvisioningHistory({accountId});
    const showTable = !history.isLoading && !history.error && history.rows.length > 0;
    const {setAdvancedFilters, defaultFilters} = history;
    const columnSizing = useDataTableColumnsWidthOnResize();
    const toolbar = useRef<HTMLDivElement>(null);
    const toolbarTop = useRef(0);
    const getHeaderTopOffset = useCallback(() => toolbarTop.current + (toolbar.current?.offsetHeight ?? 0), []);
    const advanced = useMemo(() => {
        const {email, ...filters} = history.filters;
        return filters;
    }, [history.filters]);
    const advancedKeys = Object.keys(advanced).filter(key => key !== 'period') as AdvancedFilterKey[];
    const resetAdvanced = useCallback(() => {
        setAdvancedFilters(defaultFilters);
        return defaultFilters;
    }, [setAdvancedFilters, defaultFilters]);

    return (
        <section aria-label="Provisioning History" className={provisioningSection}>
            <StickyElement
                id={`provisioning-history-controls-${accountId}`}
                ref={toolbar}
                onUpdateStickyPosition={position => {
                    toolbarTop.current = position;
                }}>
                <div className={provisioningHeader}>
                    <header className={provisioningTitle}>
                        <Group direction="h" alignItems="center">
                            <Typography fontWeight="semi-bold" variant="heading-level-3">Provisioning History</Typography>
                        </Group>
                    </header>
                    <Group direction="v" gap="m">
                        <Group direction="h" gap="m" wrap="wrap">
                            <EmailFilter
                                key={history.emailResetKey}
                                value={history.filters.email || ''}
                                onChange={history.setEmail}
                            />
                            <SectionFilter<AdvancedFilters>
                                filters={advanced}
                                defaultFilters={defaultFilters}
                                onApply={history.setAdvancedFilters}
                                onClear={resetAdvanced}
                                width={465}
                                maxHeight={600}
                                getAnchor={({anchorRef, positionRef, getAnchorProps}) => (
                                    <Button
                                        variant="Type.SECONDARY"
                                        ref={anchorRef}
                                        {...getAnchorProps()}
                                        startIcon={(
                                            <Icon type={advancedKeys.length
                                                ? Icons.FILTER_FILLED : Icons.FILTER_EMPTY}
                                            />
                                        )}
                                        endIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}>
                                        <b>Filter</b>
                                    </Button>
                                )}>
                                {(filters, onChange) => (
                                    <ProvisioningFilters
                                        filters={filters}
                                        onChange={onChange}
                                        products={history.products}
                                    />
                                )}
                            </SectionFilter>
                            <FlatButton
                                startIcon={<Icon type={Icons.TRASH}/>}
                                onClick={history.clearFilters}
                                disabled={!history.hasFilters}>
                                Clear all criteria
                            </FlatButton>
                            {!!history.error && (
                                <Button variant="Type.SECONDARY" onClick={history.retry}>
                                    Retry
                                </Button>
                            )}
                            {history.canRestart ? (
                                <Button variant="Type.SECONDARY" onClick={history.restart}>
                                    Restart from first page
                                </Button>
                            ) : null}
                        </Group>
                        <div className={provisioningPills}>
                            {advancedKeys.map(key => (
                                <StripOfPills key={key} label={FILTER_LABELS[key]}>
                                    <Pill
                                        name={getFilterCaption(key, advanced[key]!, history.products)}
                                        removable={true}
                                        preventedRemoveHandler={() => history.removeFilter(key)}
                                    />
                                </StripOfPills>
                            ))}
                        </div>
                    </Group>
                </div>
            </StickyElement>
            <ServerResponseHandler
                serverError={history.error}
                sectionName="Provisioning History"
                smallView={true}
                errorTitle="Provisioning history couldn’t be loaded."
                errorSubtitle="Try again.">
                <div className={provisioningContent}>
                    {history.isLoading ? (
                        <BoxLoading loading={true}>
                            <div className={provisioningContent}/>
                        </BoxLoading>
                    ) : null}
                    {!history.isLoading && (
                        history.rows.length ? (
                            <ProvisioningHistoryTable
                                rows={history.rows}
                                page={history.page}
                                pageSize={history.pageSize}
                                getHeaderTopOffset={getHeaderTopOffset}
                                columnSizing={columnSizing}
                                pagination={<ProvisioningPagination {...history} hasRows={history.rows.length > 0}/>}
                            />
                        ) : (
                            <NoResultPage
                                title={history.hasFilters
                                    ? 'No provisioning records match the selected filters.'
                                    : 'No provisioning history is available for this account.'}
                                description=""
                            />
                        )
                    )}
                </div>
            </ServerResponseHandler>
            {!showTable && <ProvisioningPagination {...history} hasRows={history.rows.length > 0} standalone={true}/>}
        </section>
    );
};

export const AccountProvisioningHistory = ({accountId}: {accountId: number}) => (
    <HistorySection key={accountId} accountId={accountId}/>
);
